import { MouseEvent, useState } from 'react'
import styled from 'styled-components'

import { useOutsideClick } from 'aem-libs-react-ts/src/hooks/useOutsideClick'
import { createTestId } from 'aem-libs-tests/src/utility/createTestId'
import { conditionalInvoke } from 'aem-libs-ts/src/utils/common/conditionalInvoke'

import { OpenCloseCaret } from '../../../common/open-close-caret/OpenCloseCaret'
import { ButtonHeight, ButtonMinWidth } from '../types'
import * as ButtonSelectStyled from './ButtonSelect.styled'
import { ButtonSelectItemType, ButtonSelectTheme } from './types'


// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ItemIco = styled.span<{ $icon: any }>`
  background: url(${({ $icon }) => $icon}) no-repeat center center;
  background-size: 20px 18px;
  width: 32px;
  min-width: 32px;
  height: 20px;
  display: inline-block;
`

const ItemLabel = styled.span`
  text-align: left;
`

export interface ButtonSelectProps<TValue> {
  buttonTheme: ButtonSelectTheme
  label: string
  items: ButtonSelectItemType<TValue>[]
  onClick?: (value: TValue, e: MouseEvent<HTMLAnchorElement>) => void
  onSwitchItemsVisibility?: (isOpen: boolean) => void
  testId?: string
  buttonHeight?: ButtonHeight
  buttonWidth?: ButtonMinWidth
}

export function ButtonSelect<TValue>({
  buttonTheme,
  label,
  items,
  onClick,
  onSwitchItemsVisibility,
  testId,
  buttonHeight = 'medium',
  buttonWidth = '150px'
}: ButtonSelectProps<TValue>) {
  const [isOpen, setIsOpen] = useState(false)
  const switchVisibility = () => {
    conditionalInvoke(onSwitchItemsVisibility, [!isOpen])
    setIsOpen((c) => !c)
  }

  const refEl = useOutsideClick<HTMLDivElement>({ onClick: () => setIsOpen(false) })

  const handleClickItem = (value: TValue, event: MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(value, event)
    }
  }

  const usedTestId = testId ?? 'button-select'

  return (
    <ButtonSelectStyled.Container ref={refEl} $buttonWidth={buttonWidth}>
      <ButtonSelectStyled.MainButton
        buttonHeight={buttonHeight}
        $buttonWidth={buttonWidth}
        buttonTheme={buttonTheme}
        onClick={switchVisibility}
        data-testid={createTestId(usedTestId, 'label')}
      >
        {label}
        <OpenCloseCaret
          state={isOpen ? 'open' : 'close'}
          colorTheme={buttonTheme === 'transparent' ? 'dark' : 'light'}
        />
      </ButtonSelectStyled.MainButton>
      {isOpen && (
        <ButtonSelectStyled.Items>
          <ButtonSelectStyled.ItemsInner $itemsTheme={buttonTheme} data-testid={createTestId(usedTestId, 'items')}>
            {items
              .filter((item) => item.showButton)
              .map(({ label, value, icon, onClick, href }, index) => (
                <li key={index}>
                  <ButtonSelectStyled.Item
                    href={href}
                    onClick={(e) => {
                      handleClickItem(value as TValue, e)
                      onClick && onClick(e)
                    }}
                    data-testid={createTestId(usedTestId, `item-${index}`)}
                  >
                    {icon ? <ItemIco $icon={icon} /> : null}
                    <ItemLabel>{label}</ItemLabel>
                  </ButtonSelectStyled.Item>
                </li>
              ))}
          </ButtonSelectStyled.ItemsInner>
        </ButtonSelectStyled.Items>
      )}
    </ButtonSelectStyled.Container>
  )
}
